/* Manga of the Week — shared game runtime.
 *
 * One `initMotd(root)` per `[data-motd]` element, so the full page
 * (sections/community-game.liquid) and the landing popup
 * (sections/motd-popup.liquid) can share this file — and even coexist.
 * Every query is scoped to `root`; nothing here touches document-wide state
 * except the anonymous id in localStorage.
 *
 * Contract with the markup: snippets/motd-game.liquid.
 * Contract with the popup shell: `data-motd-defer` on the root means "build
 * the markup but do NOT call /puzzle/today until root.motdStart() is invoked"
 * (a popup that never opens must never create a play row). The root emits
 * CustomEvents the popup forwards to analytics:
 *   motd:loaded {date, finished, solved} · motd:guess {attempt}
 *   motd:solved {attempt} · motd:lost · motd:claimed · motd:notify · motd:gated · motd:sms {subscribed|skipped}
 */
(function () {
  'use strict';

  // Frequency/suppression memory shared with the popup shell. The game writes
  // `finishedPuzzle` here when a round ends (on the page OR in the popup), so
  // the popup can stay quiet for someone who already finished this week.
  const POPUP_KEY = 'haki_motd_popup';
  function readPopupState() { try { return JSON.parse(localStorage.getItem(POPUP_KEY) || '{}') || {}; } catch (e) { return {}; } }
  function writePopupState(patch) {
    try { localStorage.setItem(POPUP_KEY, JSON.stringify(Object.assign(readPopupState(), patch))); } catch (e) {}
  }

  // Stable anonymous id so a logged-out player's guesses survive a refresh.
  // (Logged-in visitors are identified by Shopify via the App Proxy instead;
  // the server prefers that verified id and ignores this one.)
  const ANON_KEY = 'haki_motd_anon';
  function anonId() {
    const gen = () => 'a' + Date.now().toString(36) + Math.random().toString(16).slice(2);
    try {
      let id = localStorage.getItem(ANON_KEY);
      if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : gen()); localStorage.setItem(ANON_KEY, id); }
      return id;
    } catch (e) {
      // localStorage blocked (private mode): keep a per-session id in memory.
      return (window.__motdAnon = window.__motdAnon || gen());
    }
  }

  // 42s · 3m 05s · 2h 10m · 3d — solve times can span days on a weekly puzzle.
  function fmtDuration(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) return s + 's';
    const m = Math.floor(s / 60);
    if (m < 60) return m + 'm ' + String(s % 60).padStart(2, '0') + 's';
    const h = Math.floor(m / 60);
    if (h < 48) return h + 'h ' + (m % 60) + 'm';
    return Math.floor(h / 24) + 'd';
  }
  function fmtWeek(dateStr) {
    const d = new Date(dateStr + 'T12:00:00Z');
    if (isNaN(d.getTime())) return dateStr;
    try { return 'Week of ' + new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(d); } catch (e) { return dateStr; }
  }

  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m])); }
  const ordinal = (n) => { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

  // A won code's deadline, in the READER's timezone with the zone named.
  //
  // The deadline is one fixed instant (30 days after the win since 2026-10-06). Two
  // renderings are wrong: raw ISO, and a bare ET clock time with no zone — a
  // player in London reading "9:00 AM" plans around the wrong afternoon. Local
  // time plus a zone abbreviation is the only one true for every reader.
  //
  // Returns '' for anything unparseable, so the caller hides the line rather
  // than promising a customer "Invalid Date".
  function formatExpiry(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      }).format(d);
    } catch (e) {
      return d.toLocaleString();
    }
  }

  function initMotd(root) {
    if (root.__motdInit) return; root.__motdInit = true;
    const API = (root.dataset.api || '/apps/community').replace(/\/$/, '');
    const MODE = root.dataset.motdMode || 'page';
    // Popup A/B attribution: the popup section sets data-hk-exp to
    // "<experimentKey>:<armKey>" only for a real test assignment. It rides on
    // every sign-up request so the server can stamp that arm on the Klaviyo
    // profile (community-app src/popup-tag.js). Absent → nothing is sent.
    // (The `arm` that used to ride on game requests picked a logged-out ladder
    // A/B arm; that test was retired 2026-10-06 with the NEW/RETURNING ladder.)
    const signupBody = (o) => JSON.stringify(root.dataset.hkExp
      ? Object.assign(o, { variant: root.dataset.hkExp, exp_visitor_id: root.dataset.hkExpVisitor || undefined })
      : o);
    const COLS = parseInt(root.dataset.cols, 10) || 10;
    const ROWS = parseInt(root.dataset.rows, 10) || 14;
    const $ = (s) => root.querySelector(s);
    // After a SUCCESSFUL sign-up only: tie this browser to the Klaviyo profile so
    // Viewed Product / Added to Cart reach it (snippets/haki-klaviyo-identify.liquid).
    const identify = (email, phone) => { try { if (window.hakiIdentify) window.hakiIdentify(email, phone); } catch (e) {} };
    // True when a consent line was on screen beside the email field at submit:
    // present in the markup (Liquid omits it when the setting is blank), not
    // hidden, not empty, AND the field's aria-describedby points at it, so a
    // screen-reader user hears it with the field. Sent as consent_shown so the
    // server only makes a Shopify customer (email marketing consent) of someone
    // who was given the notice.
    const describedBy = (input) => String((input && input.getAttribute && input.getAttribute('aria-describedby')) || '').split(/\s+/).filter(Boolean);
    // Really on screen: no layout box (display:none on it or any ancestor) or
    // a hidden / display:none / visibility:hidden ancestor all count as NOT shown.
    const reallyVisible = (el) => {
      try {
        if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
        for (let n = el; n && n !== document.documentElement; n = n.parentElement) {
          if (n.hidden) return false;
          const cs = typeof getComputedStyle === 'function' ? getComputedStyle(n) : null;
          if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || cs.visibility === 'collapse')) return false;
        }
        return true;
      } catch (e) { return false; }
    };
    const consentShown = (el, input) => Boolean(el && !el.hidden && String(el.textContent || '').trim()
      && el.id && describedBy(input).includes(el.id) && reallyVisible(el));
    const emit = (name, detail) => root.dispatchEvent(new CustomEvent('motd:' + name, { detail: detail || {}, bubbles: true }));

    // Operator-editable copy (theme settings), serialized next to the markup.
    // Liquid guarantees every key has a default, so no JS-side fallbacks needed.
    let COPY = {};
    try { COPY = JSON.parse($('[data-motd-copy]').textContent); } catch (e) {}
    // Copy templates use [token] (square brackets: Shopify's Liquid validator
    // rejects a bare { inside {{ }} tags, even quoted).
    const fmt = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));

    const panel = $('[data-motd-panel]');
    const mosaic = $('[data-motd-mosaic]');
    const caption = $('[data-motd-caption]');
    const captionTitle = $('[data-motd-caption-title]');
    const ordinalEl = $('[data-motd-ordinal]');
    const segmentsEl = $('[data-motd-segments]');
    const bannerEl = $('[data-motd-banner]');
    const form = $('[data-motd-form]');
    const input = $('[data-motd-input]');
    const submitBtn = $('[data-motd-submit]');
    const suggestEl = $('[data-motd-suggest]');
    const clueEl = $('[data-motd-clue]');
    const clueLabel = $('[data-motd-clue-label]');
    const clueText = $('[data-motd-clue-text]');
    const pastEl = $('[data-motd-past]');
    const pastList = $('[data-motd-past-list]');
    const msgEl = $('[data-motd-msg]');
    const endEl = $('[data-motd-end]');
    const ladderEl = $('[data-motd-ladder]');
    const loaderEl = $('[data-motd-loading]');
    const uiEl = $('[data-motd-ui]');

    // ---------- loading state ----------
    // The markup starts in it (snippets/motd-game.liquid): the whole UI laid out
    // at its final size but hidden + inert (skeleton spacers in the ladder and
    // clue box), the haki-loader centred over the panel, the guess box
    // disabled. Two signals end it:
    //   skeletons + guess box - the /puzzle/today answer (or its failure)
    //   reveal()              - the first cover stage is in (or failed, or there
    //                           is none, or the load failed): the loader fades
    //                           out and the UI fades in (CSS, instant under
    //                           reduced motion).
    // A watchdog reveals the UI anyway after REVEAL_MAX_MS, so a hung request
    // or image can never leave a visitor looking at a spinner.
    const REVEAL_MAX_MS = 8000;
    let revealed = false, watchdog = null, dataIn = false;
    function reveal() {
      clearTimeout(watchdog);
      if (revealed) return;
      revealed = true;
      if (loaderEl) {
        loaderEl.classList.add('is-done');
        setTimeout(() => { if (revealed) loaderEl.hidden = true; }, 150);
      }
      if (uiEl) {
        uiEl.classList.remove('motd__wrap--loading');
        uiEl.removeAttribute('inert'); uiEl.inert = false;
        uiEl.removeAttribute('aria-hidden');
      }
    }
    function conceal() {
      revealed = false;
      if (loaderEl) { loaderEl.classList.remove('is-done'); loaderEl.hidden = false; }
      if (uiEl) {
        uiEl.classList.add('motd__wrap--loading');
        uiEl.setAttribute('inert', ''); uiEl.inert = true;
        uiEl.setAttribute('aria-hidden', 'true');
      }
    }
    const skels = () => [ladderEl, clueEl].filter((el) => el && el.hasAttribute('data-motd-skel'));
    function showSkeletons(on) { skels().forEach((el) => { el.hidden = !on; }); }
    function endSkeletons() {
      skels().forEach((el) => { el.removeAttribute('data-motd-skel'); el.hidden = true; });
      if (clueText) clueText.textContent = '';
      if (ladderEl && ladderEl.hidden) ladderEl.innerHTML = '';
    }
    function setGuessable(on) { input.disabled = !on; submitBtn.disabled = !on; }

    let coverShown = '', maxAttempts = 4, allClues = [], acItems = [], acIndex = -1;
    let current = null;   // last server view, for the claim step
    let viewToken = null; // the 2-second rule's token from the latest view

    const api = (path, opts) => {
      const o = Object.assign({ credentials: 'include' }, opts);
      o.headers = Object.assign({ 'Content-Type': 'application/json', 'x-anon-id': anonId() }, opts && opts.headers);
      return fetch(API + path, o).then(async (r) => ({ status: r.status, body: await r.json().catch(() => ({})) }));
    };

    // ---------- cover ----------
    // Since 2026-09-24 the SERVER paints the hidden tiles out (community-app
    // src/cover.js) and sends one image per reveal stage behind a signed URL.
    // The browser never holds the unmasked cover, and never the raw poster URL,
    // whose AniList path used to name the answer. Nothing to mask here; we just
    // show the stage we were given, swapping only once the next one has loaded.
    function setCover(path) {
      if (!path) return;
      const url = `${API}${path}&c=${COLS}&r=${ROWS}`;
      if (url === coverShown) return;
      coverShown = url;
      const probe = new Image();
      probe.onerror = () => { if (coverShown === url) reveal(); };
      probe.onload = () => {
        if (coverShown !== url) return;   // a newer stage won the race
        mosaic.style.setProperty('--poster', `url("${url}")`);
        // Reveal once the cover is decoded and painted, so the fade-in never
        // shows a blank poster frame for its first frames.
        if (!revealed) {
          const decoded = probe.decode ? probe.decode().catch(() => {}) : Promise.resolve();
          decoded.then(() => requestAnimationFrame(() => requestAnimationFrame(reveal)));
        }
        // The email-gate arm keeps the cover hidden until there IS a cover —
        // pre-email it has no puzzle, and the bare white box read as a broken
        // image (operator screenshot, 2026-08-31).
        const cover = root.querySelector('[data-motd-cover]');
        if (cover) cover.classList.add('motd__cover--ready');
        mosaic.classList.remove('motd__mosaic--swap'); void mosaic.offsetWidth; mosaic.classList.add('motd__mosaic--swap');
      };
      probe.src = url;
    }

    // ---------- render ----------
    function render(v) {
      current = v;
      if (v.rest_day) { reveal(); panel.innerHTML = '<p class="motd__msg">' + escapeHtml(COPY.restDay) + '</p>'; mosaic.style.display = 'none'; return; }
      maxAttempts = v.max_attempts || 4;
      if (v.view_token) viewToken = v.view_token;
      if (v.cover_url) setCover(v.cover_url); else reveal();

      // tracker
      ordinalEl.textContent = `${ordinal(Math.min((v.attempts_used || 0) + 1, maxAttempts))} Guess`;
      renderSegments(v);
      renderLadder(v);

      // clues
      allClues = v.clues || [];
      if (allClues.length) {
        clueEl.hidden = false;
        clueLabel.textContent = fmt(COPY.clueLabel || 'Clue [n]', { n: allClues.length });
        clueText.textContent = allClues[allClues.length - 1];
        if (allClues.length > 1) {
          pastEl.hidden = false;
          pastList.innerHTML = allClues.slice(0, -1).map((c, i) => `<li><strong>${escapeHtml(fmt(COPY.clueLabel || 'Clue [n]', { n: i + 1 }))}:</strong> ${escapeHtml(c)}</li>`).join('');
        }
      }
      if (v.finished) finish(v);
    }

    // The reward ladder explainer on the play screen, filled from the live tiers
    // so it always matches the discount the server will actually grant. Since
    // 2026-10-06 everyone is shown the NEW ladder (25/20/15) until the claim,
    // where a RETURNING player is told their lower prize (renderReward).
    // One line per paying rung: "1st guess: 25%".
    function topPrize(v) {
      // The headline / tab promise "up to [max]%": the best rung on show.
      return Math.max.apply(null, [0].concat(v.discount_tiers || []).map(Number).filter((n) => Number.isFinite(n)));
    }
    function renderLadder(v) {
      const tiers = v.discount_tiers || [];
      const max = topPrize(v);
      // The heading's [max] (and, via motd:tiers, the popup tab) state the
      // server's real top prize, not the number the theme was rendered with.
      if (max > 0) {
        root.querySelectorAll('[data-motd-max]').forEach((el) => { el.textContent = String(max); });
        emit('tiers', { max });
      }
      if (!tiers.some((p) => p > 0)) { ladderEl.hidden = true; return; }
      ladderEl.hidden = false;
      // Lead-in on its own line, then one rung per line. A cleared lead-in shows
      // just the rungs. A 0 rung wins no code, so it is not listed.
      const intro = COPY.ladderIntro ? `<span class="motd__ladder-intro">${escapeHtml(COPY.ladderIntro)}</span>` : '';
      const lines = tiers.map((pct, i) => (pct > 0
        ? `<span class="motd__ladder-tier">${escapeHtml(fmt(COPY.ladderLine || '[n] guess: [percent]%', { n: ordinal(i + 1), percent: pct }))}</span>`
        : '')).join('');
      ladderEl.innerHTML = `${intro}<span class="motd__ladder-tiers">${lines}</span>`;
    }

    function renderSegments(v) {
      const used = v.attempts_used || 0;
      const wrong = (v.guesses || []).filter((g) => !g.correct).length;
      const solvedAt = v.solved ? (v.solved_on_attempt || used) : 0;
      let html = '';
      for (let i = 1; i <= maxAttempts; i++) {
        let cls = '';
        if (v.solved && i === solvedAt) cls = 'win';
        else if (i <= wrong) cls = 'wrong';
        html += `<span class="${cls}"></span>`;
      }
      segmentsEl.innerHTML = html;
    }

    // The banner is a role="status" region that stays in the DOM (a11y review
    // of #366, WCAG 4.1.3): only its text changes, so "Not quite. You still
    // have 2 more guesses." is announced. Empty, it collapses via CSS (:empty).
    function banner(text, kind) {
      bannerEl.textContent = text || '';
      bannerEl.className = 'motd__banner' + (text ? ' motd__banner--' + (kind || 'wrong') : '');
    }

    let finished = false;
    let solvedOn = null;   // guess the round was won on, for "You got it in 2 guesses!"
    // "[guesses]" → "1 guess" / "3 guesses". Unknown → the phrase is dropped
    // ("You got it!") rather than printing a wrong count.
    const guessWord = (n) => (n === 1 ? '1 guess' : n + ' guesses');
    const fmtWin = (tpl, extra) => {
      const t = String(tpl || '');
      if (!(solvedOn > 0)) return fmt(t.replace(/\s*in \[guesses\]/, ''), extra || {});
      return fmt(t, Object.assign({ guesses: guessWord(solvedOn) }, extra || {}));
    };
    function finish(v) {
      if (v.solved) solvedOn = Number(v.solved_on_attempt) || null;
      mosaic.classList.add('done');   // full-color reveal on win/lose (the server sends the unmasked stage)
      const ans = v.answer || {};
      if (ans.title) { caption.hidden = false; captionTitle.textContent = v.solved ? ans.title : `It was ${ans.title}`; }
      // swap game panel for the end state
      root.classList.add('motd--finished');
      panel.style.display = 'none';
      endEl.hidden = false;
      $('[data-motd-end-title]').textContent = v.solved ? COPY.winTitle : COPY.loseTitle;
      $('[data-motd-end-sub]').textContent = v.solved
        ? COPY.winSub
        : fmt(COPY.loseSub, { answer: ans.title || 'revealed above' });

      renderReward(v.reward);
      const timeEl = $('[data-motd-solve-time]');
      if (timeEl) {
        timeEl.hidden = !(v.solved && v.solve_ms != null);
        if (!timeEl.hidden) timeEl.textContent = `Solved in ${fmtDuration(v.solve_ms)}`;
      }
      if (!finished) {
        finished = true;
        if (v.solved) confetti();
        setupShare(v);
        setupNotify();
        // Remember for the popup's "don't nag someone who already finished".
        writePopupState({ finishedPuzzle: v.date || null, finishedAt: Date.now() });
      }
    }

    // ---------- reward + email gate ----------
    // Server states: {code} (claimed) · {claimable, percent_off} (solved, email
    // not given yet) · {capped} · {unavailable}. The code is only ever in the
    // first one — the gate is enforced server-side, this just renders it.
    function renderReward(reward) {
      const rewardEl = $('[data-motd-reward]');
      const rewardHead = $('[data-motd-reward-head]');
      const rewardCode = $('[data-motd-reward-code]');
      const rewardExpiry = $('[data-motd-reward-expiry]');
      const claimEl = $('[data-motd-claim]');
      // Every branch below re-renders from scratch, so clear the deadline first
      // — a stale "expires Monday" left over from a previous render would sit
      // under the wrong reward state.
      if (rewardExpiry) { rewardExpiry.hidden = true; rewardExpiry.textContent = ''; }
      if (!reward) { rewardEl.hidden = true; return; }
      if (reward.code) {
        rewardEl.hidden = false; if (claimEl) claimEl.hidden = true;
        // Use the percent the server actually granted. If we do not know it,
        // say nothing rather than something wrong.
        // A RETURNING player was shown "up to 25%" and gets the lower ladder:
        // say so kindly instead of a bare, smaller number (2026-10-06).
        rewardHead.textContent = reward.percent_off && reward.player_type === 'returning' && COPY.returningHead
          ? fmt(COPY.returningHead, { percent: reward.percent_off })
          : reward.percent_off
          ? fmt(COPY.rewardHead, { percent: reward.percent_off })
          : 'You won a discount code, one-time use.';
        rewardCode.innerHTML = `<span>${escapeHtml(fmt(COPY.rewardCode, { code: reward.code }))}</span>` +
          `<button type="button" class="motd__copy" data-motd-copy-code>${escapeHtml(COPY.copyCode || 'Copy code')}</button>`;
        const cp = $('[data-motd-copy-code]');
        cp.addEventListener('click', async () => {
          try { await navigator.clipboard.writeText(reward.code); cp.textContent = COPY.copied || 'Copied!'; setTimeout(() => { cp.textContent = COPY.copyCode || 'Copy code'; }, 1600); } catch (e) {}
        });
        // The deadline (operator decision 2026-08-31: codes die at the next
        // launch). Shown only when the server actually sent one — with expiry
        // switched off the payload has no expires_at and this stays hidden, so
        // the block degrades to exactly the pre-2026-08-31 copy.
        const when = formatExpiry(reward.expires_at);
        if (rewardExpiry && when && COPY.rewardExpiry) {
          rewardExpiry.textContent = fmt(COPY.rewardExpiry, { expires: when });
          rewardExpiry.hidden = false;
        }
      } else if (reward.claimable) {
        rewardEl.hidden = false;
        rewardCode.textContent = '';
        // The heading and the form wait for the phone decision (at most the
        // 1.5s geo lookup, usually already done — it starts at boot), so a
        // visitor never sees a phone field appear and then vanish.
        phoneDecision().then((d) => {
          decided = d;
          const head = d.mode === 'required' ? (COPY.claimHeadPhone || COPY.claimHead) : COPY.claimHead;
          // The prize itself is the reveal (operator 2026-09-29): the default
          // copy names the guess count, not the percent. [percent] still works
          // for an operator who puts it back in the editor.
          rewardHead.textContent = reward.percent_off
            ? fmtWin(head, { percent: reward.percent_off })
            : fmtWin(head, { percent: '' }).replace(/\s*%\s*/, ' ');
          if (claimEl) { claimEl.hidden = false; setupClaim(); }
        });
      } else if (reward.good_job) {
        // A win on the 0% rung (4th guess): no prize, no code — so never "reveal
        // your prize". The same email form signs them up for next week's puzzle
        // (POST /puzzle/claim with no code to hand over), unless this play
        // already gave an email (signed_up: claimed, or the "email to play" gate).
        rewardEl.hidden = false;
        rewardCode.textContent = '';
        const done = Boolean(reward.signed_up);
        rewardHead.textContent = fmtWin(done ? (COPY.goodJobDone || COPY.goodJob) : COPY.goodJob);
        // One email ask on screen, not two: the separate "Get notified" form
        // below would repeat this one.
        const nb = root.querySelector('.motd__notify');
        if (nb) nb.hidden = true;
        if (claimEl) { claimEl.hidden = done; if (!done) setupClaim({ noPrize: true }); }
      } else if (reward.ip_limited) {
        rewardEl.hidden = false; if (claimEl) claimEl.hidden = true;
        rewardHead.textContent = COPY.ipLimit;
        rewardCode.textContent = '';
      } else if (reward.capped) {
        rewardEl.hidden = false; if (claimEl) claimEl.hidden = true;
        rewardHead.textContent = 'You’ve hit your win-a-code limit for now.';
        // The cap is a rolling 30 days (D21), NOT weekly. The payload is only
        // { capped: true }, so say what is true without naming a number that
        // could drift from DISCOUNT_CAP_PER_30D.
        rewardCode.textContent = 'You can win again once your recent codes are more than 30 days old.';
      } else {
        rewardEl.hidden = true;
      }
    }

    // ---------- phone mode ----------
    // required: the claim form asks for email AND a US/CA mobile, and the code
    //           comes back only when both validate (server-enforced too).
    // optional: email reveals the code, then the separate "get it by text" step.
    // off:      email only, no phone anywhere.
    // Only a visitor the snippet marked eligible (US/CA store country, popup)
    // can ever be asked; an A/B override (root data-motd-phone, set by the
    // popup section from the variant config) picks between the three for them.
    const PHONE_MODES = ['required', 'optional', 'off'];
    function phoneMode() {
      const c = $('[data-motd-claim]');
      if (!c || c.dataset.phoneEligible !== 'true') return 'off';
      const o = root.dataset.motdPhone;
      if (PHONE_MODES.indexOf(o) >= 0) return o;
      return PHONE_MODES.indexOf(c.dataset.phoneDefault) >= 0 ? c.dataset.phoneDefault : 'off';
    }
    // Where the visitor actually IS (operator, 2026-09-24). localization.country
    // is the MARKET, and a first-time visitor with no localization cookie can get
    // the primary market (US). Requiring a US/CA number from someone abroad would
    // lock them out of their code, so "required" also needs Shopify's own IP
    // detection (/browsing_context_suggestions.json) to say US or CA:
    //   detected US/CA               → required        (reason phone_required)
    //   detected elsewhere           → email only      (reason email_geo)
    //   lookup failed / slow / blank → optional, NEVER required (reason phone_fallback)
    // Only runs when the mode would otherwise be "required". One lookup per page.
    const GEO_TIMEOUT_MS = 1500;
    let geoPromise = null;
    function detectCountry() {
      if (!geoPromise) {
        const lookup = fetch('/browsing_context_suggestions.json', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then((r) => (r && r.status === 200 ? r.json() : null))
          .then((j) => { const c = j && j.detected_values && j.detected_values.country; const h = c && (c.handle || c.code); return typeof h === 'string' && /^[A-Z]{2}$/.test(h) ? h : null; })
          .catch(() => null);
        const timeout = new Promise((res) => setTimeout(() => res(null), GEO_TIMEOUT_MS));
        geoPromise = Promise.race([lookup, timeout]);
      }
      return geoPromise;
    }
    /** Resolves { mode: required|optional|off, reason } — the claim form's final say. */
    let decision = null;
    function phoneDecision() {
      if (!decision) {
        const base = phoneMode();
        if (base !== 'required') {
          decision = Promise.resolve({ mode: base, reason: base === 'optional' ? 'phone_optional' : 'email_only' });
        } else {
          decision = detectCountry().then((geo) => {
            if (geo === 'US' || geo === 'CA') return { mode: 'required', reason: 'phone_required' };
            if (geo) return { mode: 'off', reason: 'email_geo' };
            return { mode: 'optional', reason: 'phone_fallback' };
          });
        }
      }
      return decision;
    }
    let decided = null;   // the resolved decision, once known (sync read for renders)
    const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    // US/CA (NANP) only, same rule as the server's normaliseSmsPhone.
    function phoneOk(raw) {
      const d = String(raw || '').replace(/\D/g, '');
      const n = d.length === 11 && d[0] === '1' ? d.slice(1) : d;
      return n.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(n);
    }

    let claimBound = false;
    function setupClaim(opts) {
      const NO_PRIZE = Boolean(opts && opts.noPrize);
      const cForm = $('[data-motd-claim-form]');
      if (!cForm || claimBound) return;
      claimBound = true;
      const cEmail = $('[data-motd-claim-email]');
      const cPhone = $('[data-motd-claim-phone]');
      const cErr = $('[data-motd-claim-error]');
      const cCombined = $('[data-motd-claim-consent-combined]');
      const cEmailConsent = $('[data-motd-claim-consent-email]');
      const cMsg = $('[data-motd-claim-msg]');
      const cBtn = $('[data-motd-claim-submit]');
      const D = decided || { mode: phoneMode() === 'required' ? 'optional' : phoneMode(), reason: 'phone_fallback' };
      // No prize → email only: a phone number is never asked for nothing.
      const MODE_NOW = NO_PRIZE ? 'off' : D.mode;
      const REQUIRED = MODE_NOW === 'required' && Boolean(cPhone);
      if (NO_PRIZE && cBtn && COPY.goodJobButton) cBtn.textContent = COPY.goodJobButton;
      if (REQUIRED) {
        cPhone.hidden = false;
        // One combined email + SMS consent line replaces the email-only one.
        if (cCombined) cCombined.hidden = false;
        if (cEmailConsent) cEmailConsent.hidden = true;
        cForm.noValidate = true;   // our own messages, announced + tied to the field
        if (cEmail && cCombined && cCombined.id) cEmail.setAttribute('aria-describedby', cCombined.id);
      }
      // Each field's standing description: the consent line on screen. Phone →
      // the combined line; email → the combined line in phone-required mode,
      // otherwise the email-only line. Errors are prepended to these.
      const consentLine = () => (REQUIRED ? cCombined : cEmailConsent);
      const baseDesc = (el) => {
        if (el === cPhone) return cCombined ? cCombined.id : '';
        const line = consentLine();
        return el === cEmail && line && line.id && !line.hidden ? line.id : '';
      };
      function clearError() {
        if (cErr) { cErr.hidden = true; cErr.textContent = ''; }
        [cEmail, cPhone].forEach((el) => { if (!el) return; el.removeAttribute('aria-invalid'); const d = baseDesc(el); if (d) el.setAttribute('aria-describedby', d); else el.removeAttribute('aria-describedby'); });
      }
      // One visible error line, linked to the field it is about, focus moved there.
      function showError(el, text) {
        if (!cErr) { cMsg.textContent = text; return; }
        cErr.textContent = text; cErr.hidden = false;
        cErr.setAttribute('role', 'alert');
        el.setAttribute('aria-invalid', 'true');
        el.setAttribute('aria-describedby', [cErr.id, baseDesc(el)].filter(Boolean).join(' '));
        el.focus();
      }
      cForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError();
        const email = (cEmail.value || '').trim();
        const phone = REQUIRED ? (cPhone.value || '').trim() : '';
        if (REQUIRED) {
          if (!EMAIL_OK.test(email)) return showError(cEmail, COPY.emailInvalid || COPY.claimError || 'Please enter a valid email.');
          if (!phoneOk(phone)) return showError(cPhone, COPY.smsInvalid || 'Enter a US or Canadian mobile number.');
        } else if (!email) return;
        cBtn.disabled = true; cMsg.textContent = '';
        const payload = REQUIRED ? { email, phone, phone_mode: 'required', source: MODE } : { email, source: MODE };
        // The line beside the field: the combined email + SMS one when a number
        // is required, the email-only one otherwise.
        if (consentShown(consentLine(), cEmail)) payload.consent_shown = true;
        const res = await api('/api/puzzle/claim', { method: 'POST', body: signupBody(payload) });
        cBtn.disabled = false;
        if (res.status === 409 && res.body && res.body.error === 'email_used') { cMsg.textContent = COPY.emailUsed; return; }
        if (res.status === 409 && res.body && res.body.reward) { renderReward(res.body.reward); return; }   // capped / ip_limit
        if (res.status === 400 && res.body && res.body.error === 'invalid_phone' && REQUIRED) { showError(cPhone, COPY.smsInvalid || 'Enter a US or Canadian mobile number.'); return; }
        if (res.status === 400 && REQUIRED) { showError(cEmail, COPY.emailInvalid || COPY.claimError || 'Please enter a valid email.'); return; }
        if (res.status !== 200 || !res.body || !res.body.claimed) {
          cMsg.textContent = res.status === 400 ? (COPY.claimError || 'Please enter a valid email.') : COPY.error;
          return;
        }
        cMsg.textContent = COPY.claimSuccess || '';
        identify(email, phone);
        emit('claimed', { hasCode: Boolean(res.body.reward && res.body.reward.code), phoneMode: REQUIRED ? 'required' : (MODE_NOW === 'optional' ? 'optional' : 'off'), phoneReason: REQUIRED ? 'phone_required' : (NO_PRIZE ? 'email_only' : D.reason) });
        renderReward(res.body.reward);
        // The form the focus was in has just been hidden; put focus on the code
        // (its Copy button) instead of letting it fall to <body>.
        // Focus the reward line itself (WCAG 4.1.3): it carries the prize and,
        // for a returning player, why it is lower than "up to 25%". A screen
        // reader reads it; Copy code is the next Tab stop after it.
        const h = $('[data-motd-reward-head]');
        if (h && ((res.body.reward && res.body.reward.code) || NO_PRIZE)) { h.setAttribute('tabindex', '-1'); h.focus(); }
        // The separate "get it by text" ask exists only in optional mode; in
        // required mode the number was just given, so asking again would nag.
        if (MODE_NOW === 'optional' && res.body.reward && res.body.reward.code) showSms();
      });
    }

    // ---------- optional text-message step (phone mode "optional" only) ----------
    // Only ever shown AFTER a claim has put the code on screen (never instead of
    // it), and once per browser: a "Skip" or a sign-up is remembered.
    const SMS_KEY = 'haki_motd_sms';
    let smsBound = false;
    function smsDone() { try { return Boolean(localStorage.getItem(SMS_KEY)); } catch (e) { return false; } }
    function markSms(v) { try { localStorage.setItem(SMS_KEY, v); } catch (e) {} }
    function showSms() {
      const box = $('[data-motd-sms]');
      if (!box || smsDone()) return;
      box.hidden = false;
      if (smsBound) return;
      smsBound = true;
      const sForm = $('[data-motd-sms-form]');
      const sPhone = $('[data-motd-sms-phone]');
      const sBtn = $('[data-motd-sms-submit]');
      const sSkip = $('[data-motd-sms-skip]');
      const sMsg = $('[data-motd-sms-msg]');
      // Where focus goes when the form disappears, so it is never dropped on <body>.
      const refocus = () => { const t = $('[data-motd-copy-code]') || sMsg; if (t) { if (t === sMsg) sMsg.setAttribute('tabindex', '-1'); t.focus(); } };
      sSkip.addEventListener('click', () => {
        markSms('skipped');
        box.hidden = true;
        emit('sms', { skipped: true });
        refocus();
      });
      sForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const phone = (sPhone.value || '').trim();
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 10) { sMsg.textContent = COPY.smsInvalid; sPhone.setAttribute('aria-invalid', 'true'); sPhone.focus(); return; }
        sBtn.disabled = true; sMsg.textContent = ''; sPhone.removeAttribute('aria-invalid');
        let res;
        try { res = await api('/api/puzzle/sms', { method: 'POST', body: signupBody({ phone, source: MODE }) }); } catch (err) { res = { status: 0, body: {} }; }
        sBtn.disabled = false;
        if (res.status === 400 && res.body && res.body.error === 'invalid_phone') {
          sMsg.textContent = COPY.smsInvalid; sPhone.setAttribute('aria-invalid', 'true'); sPhone.focus(); return;
        }
        if (res.status !== 200 || !res.body || !res.body.subscribed) { sMsg.textContent = COPY.error; return; }
        markSms('subscribed');
        sForm.hidden = true; sSkip.hidden = true;
        sMsg.textContent = COPY.smsSuccess;
        emit('sms', { subscribed: true });
        refocus();
      });
    }

    function confetti() {
      const box = $('[data-motd-confetti]');
      const colors = [getComputedStyle(root).getPropertyValue('--motd-accent') || '#e2333a', '#16a34a', '#f59e0b', '#3b82f6', '#fff'];
      let html = '';
      for (let i = 0; i < 60; i++) {
        const left = Math.floor((i * 37) % 100), delay = ((i % 10) / 10 * 0.8).toFixed(2), dur = (1.6 + (i % 7) * 0.28).toFixed(2);
        html += `<i style="left:${left}%;background:${colors[i % colors.length]};animation-duration:${dur}s;animation-delay:${delay}s;"></i>`;
      }
      box.innerHTML = html;
    }

    function setupShare(v) {
      const btn = $('[data-motd-share]');
      if (!btn) return;
      // The popup lives on any page; share the game's own URL, not the PDP.
      const url = root.dataset.shareUrl || (location.origin + location.pathname);
      const text = `Manga of the Week ${v.date || ''}\n${v.share_grid || ''}\n${url}`;
      btn.addEventListener('click', async () => {
        try {
          if (navigator.share) { await navigator.share({ text }); }
          else { await navigator.clipboard.writeText(text); btn.textContent = COPY.copied || 'Copied!'; setTimeout(() => btn.textContent = COPY.shareButton || 'Share result', 1600); }
        } catch (e) {}
      });
    }

    // Weekly-reminder capture → the community-app server (POST /api/puzzle/notify),
    // which subscribes to the MOTW list and records the result of this round so
    // the welcome email can say the right thing. Moved off the browser-side
    // Klaviyo call on 2026-09-23: that call could set no properties and left no
    // record. Only present if a list is configured.
    let notifyBound = false;
    function setupNotify() {
      const nForm = $('[data-motd-notify-form]');
      if (!nForm || notifyBound) return;
      notifyBound = true;
      const nEmail = $('[data-motd-notify-email]');
      const nMsg = $('[data-motd-notify-msg]');
      const nBtn = $('[data-motd-notify-submit]');
      nForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = (nEmail.value || '').trim();
        if (!email) return;
        nBtn.disabled = true; nMsg.textContent = '';
        try {
          const r = await api('/api/puzzle/notify', { method: 'POST', body: signupBody(Object.assign({ email, source: MODE }, consentShown($('[data-motd-notify-consent]'), nEmail) ? { consent_shown: true } : {})) });
          if (r.status === 200 && r.body && r.body.subscribed) { nMsg.textContent = COPY.notifySuccess; identify(email); nForm.reset(); emit('notify'); }
          else { nMsg.textContent = COPY.notifyError; nBtn.disabled = false; }
        } catch (err) { nMsg.textContent = COPY.notifyError; nBtn.disabled = false; }
      });
    }

    // ---------- personal scoreboard ----------
    // GET /api/puzzle/history → { stats, rounds }. Opened from the header (page)
    // and the end screen (page + popup). Always re-fetched on open so a round
    // just finished is in it.
    function setupScoreboard() {
      const dlg = $('[data-motd-stats]');
      const body = $('[data-motd-stats-body]');
      if (!dlg || !body || typeof dlg.showModal !== 'function') {
        root.querySelectorAll('[data-motd-stats-open]').forEach((b) => { b.hidden = true; });
        return;
      }
      const open = async () => {
        body.innerHTML = '<p class="motd__stats-empty">Loading…</p>';
        try { dlg.showModal(); } catch (e) { return; }
        const res = await api('/api/puzzle/history');
        if (res.status !== 200 || !res.body || !res.body.stats) { body.innerHTML = `<p class="motd__stats-empty">${escapeHtml(COPY.error || 'Something went wrong.')}</p>`; return; }
        body.innerHTML = scoreboardHtml(res.body);
      };
      root.querySelectorAll('[data-motd-stats-open]').forEach((b) => b.addEventListener('click', open));
      dlg.querySelectorAll('[data-motd-stats-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
      if (MODE === 'page' && location.hash === '#scoreboard') open();
    }

    function scoreboardHtml(h) {
      const st = h.stats;
      const tile = (n, label) => `<div class="motd__stat"><span class="motd__stat-n">${escapeHtml(String(n))}</span><span class="motd__stat-l">${escapeHtml(label)}</span></div>`;
      const tiles = [
        tile(st.played, 'Played'),
        tile(st.win_rate + '%', 'Win rate'),
        tile(st.current_streak, 'Week streak'),
        tile(st.longest_streak, 'Best streak'),
        tile(st.fastest_ms != null ? fmtDuration(st.fastest_ms) : '-', 'Fastest solve'),
      ].join('');

      // Guess distribution, Wordle-style: one bar per guess number.
      const maxN = Math.max(maxAttempts, ...Object.keys(st.distribution || {}).map(Number));
      const top = Math.max(1, ...Object.values(st.distribution || {}));
      let dist = '';
      for (let i = 1; i <= maxN; i++) {
        const n = (st.distribution || {})[i] || 0;
        dist += `<li><span class="motd__dist-k">${i}</span><span class="motd__dist-bar" style="width:${Math.max(8, Math.round((n / top) * 100))}%">${n}</span></li>`;
      }

      const LABEL = { won: 'Won', lost: 'Missed', in_progress: 'In progress', unfinished: 'Not finished' };
      const rows = (h.rounds || []).map((r) => {
        const grid = r.grid.map((c) => (c ? '🟩' : '🟥')).join('') + '⬛'.repeat(Math.max(0, r.max_attempts - r.grid.length));
        const detail = [
          r.status === 'won' ? `Guess ${r.solved_on_attempt}` : null,
          r.solve_ms != null ? fmtDuration(r.solve_ms) : null,
          r.percent_off ? `${r.percent_off}% off` : null,
        ].filter(Boolean).join(' · ');
        const thumb = r.poster_url ? `<img src="${escapeHtml(r.poster_url)}" alt="" loading="lazy" width="36" height="54" onerror="this.style.visibility='hidden'">` : '<span class="motd__round-q" aria-hidden="true">?</span>';
        return `<li class="motd__round motd__round--${r.status}">${thumb}<div class="motd__round-main">` +
          `<p class="motd__round-title">${escapeHtml(r.title || 'This week’s manga')}</p>` +
          `<p class="motd__round-meta">${escapeHtml(fmtWeek(r.date))} · ${escapeHtml(LABEL[r.status] || r.status)}${detail ? ' · ' + escapeHtml(detail) : ''}</p>` +
          `</div><span class="motd__round-grid" aria-label="${r.grid.length} of ${r.max_attempts} guesses used">${grid}</span></li>`;
      }).join('');

      return `<div class="motd__stat-row">${tiles}</div>` +
        (st.wins ? `<h3 class="motd__stats-h">Guess distribution</h3><ol class="motd__dist">${dist}</ol>` : '') +
        `<h3 class="motd__stats-h">Past rounds</h3>` +
        (rows ? `<ul class="motd__rounds">${rows}</ul>` : '<p class="motd__stats-empty">No rounds yet - this week’s is your first.</p>') +
        // Honest about scope: logging in starts a member scoreboard that follows
        // you across devices; past logged-out rounds stay on this browser.
        (h.is_anon ? `<p class="motd__stats-note">This scoreboard lives on this browser only. Log in to start one that follows you to any device.</p>` : '');
    }

    // ---------- autocomplete ----------
    let acTimer = null;
    input.addEventListener('input', () => {
      const q = input.value.trim();
      acIndex = -1;
      clearTimeout(acTimer);
      if (q.length < 2) return hideSuggest();
      acTimer = setTimeout(async () => {
        const res = await api('/api/titles/search?q=' + encodeURIComponent(q));
        acItems = (res.body && res.body.results) || [];
        renderSuggest();
      }, 200);
    });
    function renderSuggest() {
      if (!acItems.length) return hideSuggest();
      suggestEl.hidden = false;
      suggestEl.innerHTML = acItems.map((it, i) =>
        `<li role="option" data-i="${i}" class="${i === acIndex ? 'active' : ''}">${it.poster ? `<img src="${it.poster}" alt="">` : ''}<span>${escapeHtml(it.title)}</span></li>`
      ).join('');
      suggestEl.querySelectorAll('li').forEach((li) => li.addEventListener('mousedown', (e) => {
        e.preventDefault(); pick(acItems[parseInt(li.dataset.i, 10)]);
      }));
    }
    function pick(item) { if (!item) return; input.value = item.title; hideSuggest(); input.focus(); }
    function hideSuggest() { suggestEl.hidden = true; acItems = []; acIndex = -1; }
    input.addEventListener('keydown', (e) => {
      if (suggestEl.hidden || !acItems.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); acIndex = (acIndex + 1) % acItems.length; renderSuggest(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); acIndex = (acIndex - 1 + acItems.length) % acItems.length; renderSuggest(); }
      else if (e.key === 'Enter' && acIndex >= 0) { e.preventDefault(); pick(acItems[acIndex]); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); hideSuggest(); }   // Esc closes the list only — preventDefault stops <dialog> treating it as cancel
    });
    document.addEventListener('click', (e) => { if (!root.contains(e.target)) hideSuggest(); });

    // ---------- guess ----------
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const guess = input.value.trim();
      if (!guess) return;
      hideSuggest(); submitBtn.disabled = true; msgEl.textContent = '';
      const send = () => api('/api/puzzle/guess', { method: 'POST', body: JSON.stringify({ guess, view_token: viewToken }) });
      let res = await send();
      // The 2-second rule (server-side): a guess sooner than that after the
      // view is refused. A person almost never hits it; if they do, wait out
      // the remainder and send the same guess — they never see an error.
      if (res.status === 429 && res.body && res.body.error === 'too_fast') {
        await new Promise((r) => setTimeout(r, Math.min(3000, (res.body.retry_after_ms || 0) + 50)));
        res = await send();
      }
      // An old or missing token (e.g. a tab from before a deploy): the 409
      // carries a fresh view + token, so adopt it and send the guess again.
      if (res.status === 409 && res.body && res.body.error === 'stale_view') {
        if (res.body.view_token) viewToken = res.body.view_token;
        await new Promise((r) => setTimeout(r, 2100));
        res = await send();
      }
      submitBtn.disabled = false;
      if (res.status === 409) { render(res.body); return; }
      if (res.status !== 200) { msgEl.textContent = COPY.error; return; }
      input.value = '';
      const v = res.body;
      emit('guess', { attempt: v.attempts_used || 0 });
      if (v.correct === false && !v.exhausted) {
        const left = (v.max_attempts || maxAttempts) - (v.attempts_used || 0);
        banner(fmt(COPY.wrongBanner, { left, plural: left === 1 ? 'guess' : 'guesses' }), 'wrong');
      } else banner('');
      if (v.correct) emit('solved', { attempt: v.solved_on_attempt || v.attempts_used || 0 });
      else if (v.exhausted) emit('lost', { attempt: v.attempts_used || 0 });
      render(v);
      // The round just ended and finish() hid the panel the focus was in: move
      // focus to the end title ("Congrats!" / "Out of guesses") so it is read
      // out, instead of letting it fall to <body> (WCAG 2.4.3 / 4.1.3).
      if (v.finished || v.correct || v.exhausted) {
        const t = $('[data-motd-end-title]');
        if (t) { t.setAttribute('tabindex', '-1'); t.focus(); }
      }
    });

    // ---------- how-to-play popup (full page only) ----------
    // Native <dialog>.showModal() → focus trap, Esc-to-close and aria-modal for
    // free. Auto-open frequency is throttled per browser via localStorage.
    (function () {
      const howto = $('[data-motd-howto]');
      if (!howto || typeof howto.showModal !== 'function') return;
      const KEY = 'haki_motd_howto_seen';
      const freq = howto.dataset.freq || 'daily';
      const seenAt = () => { try { return parseInt(localStorage.getItem(KEY), 10) || 0; } catch (e) { return 0; } };
      const mark = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch (e) {} };
      const shouldAutoOpen = () =>
        freq === 'always' ? true :
        freq === 'once' ? !seenAt() :
        (Date.now() - seenAt()) > 864e5; // daily
      howto.addEventListener('close', mark);
      howto.querySelectorAll('[data-motd-howto-close]').forEach((b) => b.addEventListener('click', () => howto.close()));
      // click on the backdrop (= the dialog element itself, not its children) closes
      howto.addEventListener('click', (e) => { if (e.target === howto) howto.close(); });
      const opener = $('[data-motd-howto-open]');
      if (opener) opener.addEventListener('click', () => howto.showModal());
      if (shouldAutoOpen()) { try { howto.showModal(); } catch (e) {} }
    })();

    // ---------- "email to play" gate (A/B arm) ----------
    // Opt-in per instance via data-motd-gate="email" — absent means the default
    // arm and NOTHING below runs, so the control path is untouched.
    //
    // The gate stands in front of /puzzle/today, not just in front of the input:
    // creating a play row for someone who never gave an email would put them in
    // the denominator of the other arm's funnel too.
    const GATE = root.dataset.motdGate === 'email';
    const GATE_KEY = 'haki_motd_gated';
    const alreadyGated = () => { try { return localStorage.getItem(GATE_KEY) === '1'; } catch (e) { return false; } };
    const markGated = () => { try { localStorage.setItem(GATE_KEY, '1'); } catch (e) {} };
    let gateBound = false;
    let focusWhenReady = false;

    function showGate() {
      const gate = $('[data-motd-gate]');
      const ac = $('[data-motd-ac]');
      const tracker = root.querySelector('.motd__tracker');
      if (!gate) return false;
      gate.hidden = false;
      if (ac) ac.hidden = true;
      if (tracker) tracker.hidden = true;
      showSkeletons(false);   // no puzzle is loading behind the gate yet
      reveal();               // the gate IS the UI for now: show it

      const form = $('[data-motd-gate-form]');
      const email = $('[data-motd-gate-email]');
      const msg = $('[data-motd-gate-msg]');
      const btn = $('[data-motd-gate-submit]');
      if (!form) return false;
      // Bind ONCE. showGate() runs on every motdStart(), and the popup shell
      // calls motdStart() on every open — so a visitor who closes the popup
      // before answering and reopens it from the tab would otherwise submit
      // through two handlers: two /puzzle/gate POSTs (racing into two play
      // rows for one person) and two `gate` analytics events. Seen live
      // 2026-08-28 (view > close > view(tab) > gate > gate).
      if (gateBound) return true;
      gateBound = true;
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const value = (email.value || '').trim();
        if (!value) { msg.textContent = COPY.gateError; return; }
        btn.disabled = true; msg.textContent = '';
        const res = await api('/api/puzzle/gate', { method: 'POST', body: signupBody(Object.assign({ email: value, source: MODE }, consentShown($('[data-motd-gate-consent]'), email) ? { consent_shown: true } : {})) });
        btn.disabled = false;
        if (res.status === 409 && res.body && res.body.error === 'email_already_played') { msg.textContent = COPY.emailPlayed; return; }
        if (res.status !== 200 || !res.body || !res.body.gated) {
          msg.textContent = res.status === 400 ? COPY.gateError : COPY.error;
          return;
        }
        markGated();
        identify(value);
        emit('gated', {});
        gate.hidden = true;
        if (ac) ac.hidden = false;
        if (tracker) tracker.hidden = false;
        focusWhenReady = true;   // the guess box is disabled until the puzzle is in
        realStart();
      });
      return true;
    }

    // ---------- scoreboard ----------
    setupScoreboard();

    // ---------- boot ----------
    let started = false;
    function realStart() {
      if (started) return; started = true;
      showSkeletons(true);
      if (revealed) conceal();   // after the email gate: black out again while the puzzle loads
      const loadError = COPY.loadError || 'Could not load this week’s puzzle.';
      const failed = () => {
        endSkeletons();
        msgEl.textContent = loadError;
        reveal();
      };
      watchdog = setTimeout(() => {
        if (!dataIn) { endSkeletons(); msgEl.textContent = loadError; }
        reveal();
      }, REVEAL_MAX_MS);
      api('/api/puzzle/today?source=' + encodeURIComponent(MODE)).then((res) => {
        if (res.status !== 200) { failed(); return; }
        dataIn = true;
        if (msgEl.textContent === loadError) msgEl.textContent = '';   // late answer after the watchdog
        // Same task as render(): the skeletons and the real lines swap in one
        // frame, so the reserved space is handed over, never collapsed.
        endSkeletons();
        setGuessable(true);
        if (focusWhenReady) { focusWhenReady = false; try { input.focus(); } catch (e) {} }
        render(res.body);
        const loaded = { date: res.body.date || null, finished: Boolean(res.body.finished), solved: Boolean(res.body.solved), restDay: Boolean(res.body.rest_day) };
        phoneDecision().then((d) => emit('loaded', Object.assign(loaded, { phoneMode: d.mode, phoneReason: d.reason })));
      }, failed);   // network error / offline: same failure path, never an endless loader
    }
    function start() {
      phoneDecision();   // start the geo lookup now, so the claim form rarely waits
      // A returning player who already paid the email is never asked again.
      if (GATE && !alreadyGated()) { if (showGate()) return; }
      realStart();
    }
    root.motdStart = start;
    if (!root.hasAttribute('data-motd-defer')) start();
  }

  window.HakiMotd = { init: initMotd, anonId: anonId, readPopupState: readPopupState, writePopupState: writePopupState };
  const boot = () => document.querySelectorAll('[data-motd]').forEach(initMotd);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
